import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { staffChatApi } from "@/api";
import type { ChatJob } from "@/types";
import { useAuth } from "@/staff/auth/AuthContext";

/**
 * The shared chat feed (Staff Chat Brief 01 §4.1): one poll of
 * {Jobs}/staff/chat/jobs feeds the nav badge and both split-view sidebars.
 * Polls every 60s while the tab is visible (paused when hidden, immediate
 * refresh on return) — same discipline as the letters UnreadProvider. Errors
 * keep the last good value rather than flickering the badge to zero.
 *
 * Also holds the selected job id so the selection survives switching between
 * Messages and Documents (§4.2 "shared route param or store" — store).
 */

const POLL_MS = 60_000;

interface ChatFeedState {
  /** null until the first successful fetch. */
  jobs: ChatJob[] | null;
  /** Sum of unread across jobs — the Chat menu badge (§4.1). */
  totalUnread: number;
  refresh: () => Promise<void>;
  /** Zero a job's unread immediately after opening its thread — the menu badge
   *  recomputes locally without waiting for the next poll (§4.3). */
  markReadLocally: (jobId: number) => void;
  selectedJobId: number | null;
  setSelectedJobId: (id: number | null) => void;
}

const ChatCtx = createContext<ChatFeedState>({
  jobs: null,
  totalUnread: 0,
  refresh: async () => {},
  markReadLocally: () => {},
  selectedJobId: null,
  setSelectedJobId: () => {},
});

export function ChatFeedProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;
  const [jobs, setJobs] = useState<ChatJob[] | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) {
      setJobs(null);
      return;
    }
    try {
      setJobs(await staffChatApi.listChatJobs());
    } catch {
      /* keep the last good value rather than flicker to zero */
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setJobs(null);
      return;
    }
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (!interval) interval = setInterval(() => void refresh(), POLL_MS);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void refresh(); // instant on return
        start();
      }
    };

    void refresh();
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userId, refresh]);

  const markReadLocally = useCallback((jobId: number) => {
    setJobs((prev) =>
      prev ? prev.map((j) => (j.job_id === jobId ? { ...j, unread_count: 0 } : j)) : prev,
    );
  }, []);

  const totalUnread = useMemo(
    () => (jobs ?? []).reduce((sum, j) => sum + (j.unread_count || 0), 0),
    [jobs],
  );

  const value = useMemo<ChatFeedState>(
    () => ({ jobs, totalUnread, refresh, markReadLocally, selectedJobId, setSelectedJobId }),
    [jobs, totalUnread, refresh, markReadLocally, selectedJobId],
  );
  return <ChatCtx.Provider value={value}>{children}</ChatCtx.Provider>;
}

export function useChatFeed(): ChatFeedState {
  return useContext(ChatCtx);
}